import AppDataService from "./AppDataService";

// Build a query string from a params object, dropping empty/null/undefined values
const buildQueryString = (params = {}) => {
  const query = Object.entries(params)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    )
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
  return query ? `?${query}` : "";
};

export default class ReportsService {
  // Inventory Report - current stock per item across all locations (or a single facility)
  static async getInventoryReport(data = {}) {
    const queryString = buildQueryString({
      pageNumber: data?.pageNumber,
      limit: data?.limit,
      facilityId: data?.facilityId,
      searchValue: data?.searchValue,
      type: data?.type,
      status: data?.status,
    });
    return await AppDataService.get(`reports/inventory${queryString}`);
  }

  // Incident Report - visits with a non-empty incident location
  static async getIncidentReport(data = {}) {
    const queryString = buildQueryString({
      pageNumber: data?.pageNumber,
      limit: data?.limit,
      facilityId: data?.facilityId,
      searchValue: data?.searchValue,
      incidentLocation: data?.incidentLocation,
      visitType: data?.visitType,
      startDate: data?.startDate,
      endDate: data?.endDate,
    });
    return await AppDataService.get(`reports/incident${queryString}`);
  }

  // Usage Report - quantity of each item/medication issued, grouped by item + location
  static async getUsageReport(data = {}) {
    const queryString = buildQueryString({
      pageNumber: data?.pageNumber,
      limit: data?.limit,
      facilityId: data?.facilityId,
      searchValue: data?.searchValue,
      type: data?.type,
      startDate: data?.startDate,
      endDate: data?.endDate,
    });
    return await AppDataService.get(`reports/usage${queryString}`);
  }

  // Daily Orders & Medications Report - all orders or all medications across facilities
  static async getDailyOrdersReport(data = {}) {
    const queryString = buildQueryString({
      pageNumber: data?.pageNumber,
      limit: data?.limit,
      category: data?.category,
      facilityId: data?.facilityId,
      startDate: data?.startDate,
      endDate: data?.endDate,
    });
    return await AppDataService.get(`reports/daily-orders${queryString}`);
  }

  // EMS Daily Report - EMS visits by created date (EMS_Visit + Scene)
  static async getEmsDailyReport(data = {}) {
    const queryString = buildQueryString({
      pageNumber: data?.pageNumber,
      limit: data?.limit,
      facilityId: data?.facilityId,
      startDate: data?.startDate,
      endDate: data?.endDate,
    });
    return await AppDataService.get(`reports/ems-daily${queryString}`);
  }

  // Facility Patient History Report - every patient ever moved INTO a facility,
  // regardless of final disposition (includes transferred-out / discharged).
  static async getFacilityPatientHistory(data = {}) {
    const queryString = buildQueryString({
      pageNumber: data?.pageNumber,
      limit: data?.limit,
      facilityId: data?.facilityId,
      seenFacility: data?.seenFacility,
      currentFacility: data?.currentFacility,
      searchValue: data?.searchValue,
    });
    return await AppDataService.get(
      `reports/facility-patient-history${queryString}`,
    );
  }

  // ICD-10 / DSM Report - which facility has seen/used each diagnosis code.
  // Facility scoping uses `seenFacility`, not `facilityId`: the backend ignores
  // `facilityId` on this endpoint so an auto-appended value can never silently
  // narrow the report (same arrangement as Facility Patient History).
  static async getIcdReport(data = {}) {
    const queryString = buildQueryString({
      pageNumber: data?.pageNumber,
      limit: data?.limit,
      seenFacility: data?.seenFacility,
      codeType: data?.codeType,
      searchValue: data?.searchValue,
      startDate: data?.startDate,
      endDate: data?.endDate,
    });
    return await AppDataService.get(`reports/icd${queryString}`);
  }

  // Provider Patient Log - every patient a provider saw in a date range, one
  // row per encounter. Returns encounter rows only; the tab fills its provider
  // picker from the shared user list (`GET user`).
  //
  // Facility scoping uses `seenFacility`, not `facilityId`: the backend ignores
  // `facilityId` here so the globally appended value can never silently narrow
  // the log (same arrangement as the ICD report).
  static async getProviderPatientLog(data = {}) {
    const queryString = buildQueryString({
      pageNumber: data?.pageNumber,
      limit: data?.limit,
      providerId: data?.providerId,
      seenFacility: data?.seenFacility,
      searchValue: data?.searchValue,
      startDate: data?.startDate,
      endDate: data?.endDate,
    });
    return await AppDataService.get(
      `reports/provider-patient-log${queryString}`,
    );
  }

  // Patient Facility History - a single patient's full facility movement trail
  static async getPatientFacilityHistory(patientId) {
    return await AppDataService.get(
      `reports/patient-facility-history/${patientId}`,
    );
  }
}
